import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/store/ProductCard";
import { Reveal } from "@/components/store/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 text-center text-bark" data-testid="product-loading">Loading…</div>;
  if (!product) return <div className="pt-40 text-center text-bark" data-testid="product-not-found">Product not found.</div>;

  const soldOut = (product.stock ?? 0) <= 0;
  const onSale = product.salePrice && product.salePrice < product.price;
  const price = product.salePrice || product.price;

  const handleAdd = () => {
    add(product, qty);
    toast.success(`${product.name} added to bag`);
  };

  return (
    <div className="pt-24 md:pt-28 pb-28 md:pb-24" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Link to="/shop" className="text-bark hover:text-terracotta text-sm mb-6 inline-block">← Back to shop</Link>
        <div className="grid md:grid-cols-2 gap-8 md:gap-16">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] bg-sand aspect-[4/5] md:sticky md:top-28 h-fit"
          >
            <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover" />
            {onSale && !soldOut && (
              <span className="absolute top-4 left-4 bg-terracotta text-bone text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">Sale</span>
            )}
            {soldOut && (
              <span className="absolute top-4 left-4 bg-espresso text-bone text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">Sold out</span>
            )}
          </motion.div>

          {/* Info */}
          <div className="md:py-4">
            <p className="overline text-terracotta mb-3">{product.category}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-espresso tracking-tight leading-none mb-4" data-testid="product-name">
              {product.name}
            </h1>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-2xl font-semibold text-espresso" data-testid="product-price">{naira(price)}</span>
              {onSale && <span className="text-bark line-through">{naira(product.price)}</span>}
            </div>
            <p className="text-bark leading-relaxed mb-4">{product.description}</p>
            {product.benefits && (
              <p className="text-espresso font-medium mb-8 flex items-start gap-2">
                <Check className="w-5 h-5 text-moss mt-0.5 shrink-0" /> {product.benefits}
              </p>
            )}

            {/* Qty + Add (desktop) */}
            <div className="hidden md:flex items-center gap-4 mb-8">
              <div className="flex items-center border border-sandline rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-terracotta" data-testid="qty-decrease">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-terracotta" data-testid="qty-increase">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={soldOut}
                data-testid="add-to-cart-button"
                className="flex-1 bg-terracotta text-bone px-8 py-4 rounded-full font-medium hover:bg-espresso transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {soldOut ? "Sold out" : `Add to bag — ${naira(price * qty)}`}
              </button>
            </div>

            {/* Accordions */}
            <Accordion type="single" collapsible defaultValue="how" className="border-t border-sandline">
              {product.howToUse && (
                <AccordionItem value="how" className="border-sandline">
                  <AccordionTrigger className="font-serif text-lg text-espresso hover:no-underline" data-testid="accordion-howto">
                    How to use
                  </AccordionTrigger>
                  <AccordionContent className="text-bark leading-relaxed">{product.howToUse}</AccordionContent>
                </AccordionItem>
              )}
              {product.ingredients && (
                <AccordionItem value="ing" className="border-sandline">
                  <AccordionTrigger className="font-serif text-lg text-espresso hover:no-underline" data-testid="accordion-ingredients">
                    Ingredients
                  </AccordionTrigger>
                  <AccordionContent className="text-bark leading-relaxed">{product.ingredients}</AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
            <p className="text-bark text-xs mt-4">
              For external use only. Results vary by skin type — always patch test.{" "}
              <Link to="/policies/disclaimer" className="underline hover:text-terracotta" data-testid="product-disclaimer-link">
                Read our Skincare Disclaimer
              </Link>
            </p>
          </div>
        </div>

        {/* Related */}
        {product.related?.length > 0 && (
          <div className="mt-24" data-testid="related-section">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-espresso tracking-tight mb-10">You may also love</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {product.related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile add-to-cart bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-bone/95 backdrop-blur-md border-t border-sandline p-3 flex items-center gap-3" data-testid="mobile-sticky-bar">
        <div className="flex items-center border border-sandline rounded-full">
          <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5" data-testid="mobile-qty-decrease"><Minus className="w-4 h-4" /></button>
          <span className="w-7 text-center text-sm">{qty}</span>
          <button onClick={() => setQty((q) => q + 1)} className="p-2.5" data-testid="mobile-qty-increase"><Plus className="w-4 h-4" /></button>
        </div>
        <button
          onClick={handleAdd}
          disabled={soldOut}
          data-testid="mobile-add-to-cart"
          className="flex-1 bg-terracotta text-bone py-3.5 rounded-full font-medium disabled:opacity-40"
        >
          {soldOut ? "Sold out" : `Add — ${naira(price * qty)}`}
        </button>
      </div>
    </div>
  );
}
